import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { solutionBenefits } from '../data/solutionBenefits';
import { renderEmphasis } from '../utils/emphasis';
const SIMPLE_IMAGE_URL = 'https://cdn.magicpatterns.com/uploads/fS9tfZYemryx6dQ9sUJyTT/simple_(2).png';
const HIGHLIGHT_STYLE: React.CSSProperties = {
  backgroundImage: 'linear-gradient(90deg, rgba(248,164,0,0.26) 0%, rgba(248,164,0,0.18) 60%, rgba(248,164,0,0.06) 100%)',
  backgroundSize: '100% 80%',
  backgroundPosition: 'center 65%',
  backgroundRepeat: 'no-repeat'
};
export function Solution() {
  const {
    t,
    lang
  } = useLanguage();
  return <section aria-labelledby="solution-title" className="mb-20 w-full rounded-t-[28px] bg-[#F3EEE6] sm:mb-24 sm:rounded-t-[40px] lg:mb-28" data-id="element-211" data-mp="msmv7dshvkwv">
      <div className="mx-auto max-w-[1080px] px-5 py-12 sm:px-10 sm:py-16 lg:px-12 lg:py-20" data-id="element-212" data-mp="2c94b4lgej6yo">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.5
      }} transition={{
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }} data-id="element-213" data-mp="2h6o30o60vi93">
          <span className="inline-block rounded-md border border-[rgba(248,164,0,0.45)] px-2.5 py-1 text-[11px] font-medium uppercase tracking-[0.08em] text-blue-reways-950-lock" data-id="element-214" data-mp="gqk06onz93it">
            {t.solution.eyebrow}
          </span>
          <div className="mt-4 grid gap-6 lg:grid-cols-[1.25fr_1fr] lg:items-center lg:gap-12" data-id="element-215" data-mp="3k6ikckfrkx7f">
            <h2 id="solution-title" className="font-hero text-[30px] font-medium leading-[1.2] text-blue-reways-950-lock sm:text-[38px]" data-id="element-216" data-mp="3g6grn0s0be4a">
              {t.solution.titleBefore}{' '}
              <span className="box-decoration-clone px-1 text-[#F2A007]" style={HIGHLIGHT_STYLE} data-id="element-217" data-mp="3p4sdkphxhnqe">
                {t.solution.highlight}
              </span>{' '}
              {t.solution.titleAfter}
            </h2>
            <p className="text-[14px] leading-[1.7] text-grey-800-lock" data-id="element-218" data-mp="14i8ozv1lb9v0">
              {t.solution.intro.map((line, i) => <span key={i} className="block" data-id="element-219" data-mp="1gifhd5spzczr" data-mpi={i}>
                  {renderEmphasis(line)}
                </span>)}
            </p>
          </div>
        </motion.div>

        <motion.ul initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        amount: 0.2
      }} transition={{
        duration: 0.55,
        delay: 0.1,
        ease: [0.22, 1, 0.36, 1]
      }} className="mt-10 grid gap-5 sm:mt-14 md:grid-cols-2 lg:gap-8" data-id="element-220" data-mp="1lffo7ixe8jrx">
          {solutionBenefits.map(benefit => <li key={benefit.id} className="flex flex-col gap-5 rounded-[12px] bg-white p-6 shadow-[0_18px_45px_rgba(8,8,82,0.06)] sm:flex-row sm:items-center sm:gap-7 sm:p-7" data-id="element-221" data-mp="12q901gdtyzyq">
              <img src={benefit.illustration} alt="" aria-hidden="true" loading="lazy" className="h-20 w-auto self-start object-contain sm:h-24 sm:w-32 sm:shrink-0 sm:self-center" data-id="element-222" data-mp="a23eireyd6xu" />
              <div data-id="element-223" data-mp="2k5h3la1pmaz">
                <h3 className="font-hero text-[20px] font-medium leading-[1.25] text-blue-reways-950-lock" data-id="element-224" data-mp="2jlx7z0u4nub">
                  {benefit.title[lang]}
                </h3>
                <p className="mt-3 text-[13px] leading-[1.6] text-grey-800-lock" data-id="element-225" data-mp="3op0tlfdo5yf6">{benefit.text[lang]}</p>
              </div>
            </li>)}

          {/* Carte photo « Simple dès le premier jour » */}
          <li className="relative isolate min-h-[170px] overflow-hidden rounded-[12px] shadow-[0_18px_45px_rgba(8,8,82,0.1)]" data-id="element-226" data-mp="3fw5vr6qj74zf">
            <img src={SIMPLE_IMAGE_URL} alt={t.solution.simpleAlt} loading="lazy" className="absolute inset-0 h-full w-full object-cover" data-id="element-227" data-mp="3awb4h22ugddu" />
            <div aria-hidden="true" className="absolute inset-0 bg-[linear-gradient(0deg,rgba(30,30,110,0.75)_0%,rgba(30,30,110,0.2)_70%)]" data-id="element-228" data-mp="312av2o0j4lzb" />
            <div className="relative flex h-full flex-col justify-end p-6 sm:p-7" data-id="element-229" data-mp="3lrlpue1543qc">
              <h3 className="font-hero text-[20px] font-medium leading-[1.4] text-white" data-id="element-230" data-mp="3qo3ttbonby1m">
                <Sparkles className="mr-2 inline-block h-5 w-5 -translate-y-0.5 fill-none text-white" aria-hidden="true" data-id="element-231" data-mp="3cwfdew7d6neu" />
                {t.solution.simpleTitleBefore}{' '}
                <span className="box-decoration-clone px-1 text-[#F9BE52]" style={{
                ...HIGHLIGHT_STYLE,
                backgroundImage: 'linear-gradient(90deg, rgba(248,164,0,0.38) 0%, rgba(248,164,0,0.26) 60%, rgba(248,164,0,0.08) 100%)'
              }} data-id="element-232" data-mp="33141smr95wbb">
                  {t.solution.simpleTitleHighlight}
                </span>{' '}
                {t.solution.simpleTitleAfter}
              </h3>
              <p className="mt-2 text-[13px] leading-[1.6] text-white/80" data-id="element-233" data-mp="w68oxuh944wd">{t.solution.simpleText}</p>
            </div>
          </li>
        </motion.ul>
      </div>
    </section>;
}